import React, { useState, useEffect } from 'react';
import { BeakerIcon, ChartBarIcon, CurrencyDollarIcon, CircleStackIcon, SparklesIcon, FunnelIcon } from '@heroicons/react/24/outline';
import TopologyGraph from '../components/TopologyGraph';
import CapabilityPackCard, { CapabilityPack } from '../components/CapabilityPackCard';
import AgentMethodsDisplay, { AgentMethod } from '../components/AgentMethodsDisplay';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

/**
 * Model Configuration A/B/C Testing Console
 *
 * Integrated dashboard for:
 * - Agent model configurations with A/B testing support
 * - A/B test performance metrics (last 30 days)
 * - Cost vs accuracy analysis (last 7 days)
 * - Agent topology visualization
 */

interface AgentConfiguration {
  agent_id: string;
  agent_name: string;
  model_configuration: {
    default_model?: {
      provider: string;
      model: string;
      temperature?: number;
    };
    ab_testing?: {
      enabled: boolean;
      variants?: {
        A?: { model: string; traffic_percent: number; priority: string };
        B?: { model: string; traffic_percent: number; priority: string };
        C?: { model: string; traffic_percent: number; priority: string };
      };
    };
    performance_targets?: {
      max_cost_per_call?: number;
      min_success_rate?: number;
    };
  };
  circuit_breaker_status?: string;
  circuit_breaker_failures?: number;
}

interface ABTestResult {
  agent_id: string;
  method_name?: string;
  variant_name: string;
  provider: string;
  model_name: string;
  execution_count: number;
  avg_duration_ms: number;
  avg_cost_usd: number;
  total_cost_usd: number;
  success_rate: number;
}

interface CostAccuracyResult {
  agent_id: string;
  method_name?: string;
  variant_name: string;
  avg_cost: number;
  accuracy: number;
  cost_accuracy_ratio: number;
  sample_size: number;
}

const ModelConfigPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'configs' | 'packs' | 'results' | 'analysis' | 'topology'>('configs');

  // State for agent configurations
  const [agents, setAgents] = useState<AgentConfiguration[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [agentMethods, setAgentMethods] = useState<AgentMethod[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // State for capability packs
  const [capabilityPacks, setCapabilityPacks] = useState<CapabilityPack[]>([]);
  const [loadingPacks, setLoadingPacks] = useState(false);

  // State for A/B test results
  const [abResults, setAbResults] = useState<ABTestResult[]>([]);
  const [loadingAbResults, setLoadingAbResults] = useState(false);
  const [totalExecutions, setTotalExecutions] = useState(0);
  const [avgSuccessRate, setAvgSuccessRate] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [avgCost, setAvgCost] = useState(0);

  // State for cost/accuracy analysis
  const [costAccuracyResults, setCostAccuracyResults] = useState<CostAccuracyResult[]>([]);
  const [loadingCostAccuracy, setLoadingCostAccuracy] = useState(false);

  // State for topology visualization
  const [topologyData, setTopologyData] = useState<any>(null);
  const [loadingTopology, setLoadingTopology] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // Load agent configurations
  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      setError(null);

      // Use workflow-palette endpoint (agents/configurations endpoint doesn't exist yet)
      const response = await Make_Authorized_API_Call<AgentConfiguration>(
        API_ENDPOINTS.AGENTS.WORKFLOW_PALETTE,
        'GET'
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to load agents');
      }

      if (response.data?.Records) {
        setAgents(response.data.Records);
      }
    } catch (err) {
      console.error('Error loading agents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load agents');
      setAgents([]); // Empty state - fail loud, don't hide failure
    } finally {
      setLoadingAgents(false);
    }
  };

  // Load A/B test results
  const loadABResults = async () => {
    try {
      setLoadingAbResults(true);
      setError(null);

      const response = await Make_Authorized_API_Call<ABTestResult>(
        API_ENDPOINTS.AGENTS.AB_RESULTS,
        'GET'
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to load A/B results');
      }

      if (response.data?.Records) {
        setAbResults(response.data.Records);

        // Calculate summary statistics
        const totalExec = response.data.Records.reduce(
          (sum: number, r: ABTestResult) => sum + parseInt(r.execution_count.toString()),
          0
        );
        const totalCostVal = response.data.Records.reduce(
          (sum: number, r: ABTestResult) => sum + parseFloat(r.total_cost_usd.toString() || '0'),
          0
        );
        const avgSuccessRateVal =
          response.data.Records.reduce(
            (sum: number, r: ABTestResult) => sum + parseFloat(r.success_rate.toString() || '0'),
            0
          ) / (response.data.Records.length || 1);
        const avgCostVal = totalCostVal / (totalExec || 1);

        setTotalExecutions(totalExec);
        setTotalCost(totalCostVal);
        setAvgSuccessRate(avgSuccessRateVal);
        setAvgCost(avgCostVal);
      }
    } catch (err) {
      console.error('Error loading A/B results:', err);
      setError(err instanceof Error ? err.message : 'Failed to load A/B results');
      // No mock data - show error state to user
      setAbResults([]);
      setTotalExecutions(0);
      setAvgSuccessRate(0);
      setTotalCost(0);
      setAvgCost(0);
    } finally {
      setLoadingAbResults(false);
    }
  };

  // Load cost/accuracy analysis
  const loadCostAccuracy = async () => {
    try {
      setLoadingCostAccuracy(true);
      setError(null);

      const response = await Make_Authorized_API_Call<CostAccuracyResult>(
        API_ENDPOINTS.AGENTS.COST_ACCURACY,
        'GET'
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to load cost/accuracy analysis');
      }

      if (response.data?.Records) {
        setCostAccuracyResults(response.data.Records);
      }
    } catch (err) {
      console.error('Error loading cost/accuracy analysis:', err);
      setError(err instanceof Error ? err.message : 'Failed to load cost/accuracy analysis');
      setCostAccuracyResults([]); // Empty state - fail loud, don't hide failure
    } finally {
      setLoadingCostAccuracy(false);
    }
  };

  // Load topology data
  const loadTopology = async () => {
    try {
      setLoadingTopology(true);
      setError(null);

      const response = await Make_Authorized_API_Call<any>(
        API_ENDPOINTS.AGENTS.TOPOLOGY,
        'GET'
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to load topology');
      }

      setTopologyData(response.data);
    } catch (err) {
      console.error('Error loading topology:', err);
      setError(err instanceof Error ? err.message : 'Failed to load topology');
      setTopologyData(null); // Empty state - fail loud, don't hide failure
    } finally {
      setLoadingTopology(false);
    }
  };

  // Load capability packs
  const loadCapabilityPacks = async () => {
    try {
      setLoadingPacks(true);
      setError(null);

      const response = await Make_Authorized_API_Call<CapabilityPack>(
        API_ENDPOINTS.AGENTS.CAPABILITY_PACKS,
        'GET'
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to load capability packs');
      }

      if (response.data?.Records) {
        setCapabilityPacks(response.data.Records);
      }
    } catch (err) {
      console.error('Error loading capability packs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load capability packs');
      setCapabilityPacks([]); // Empty state - fail loud, don't hide failure
    } finally {
      setLoadingPacks(false);
    }
  };

  // Load agent methods
  const loadAgentMethods = async (Agent_ID: string) => {
    try {
      setLoadingMethods(true);
      setError(null);

      const response = await Make_Authorized_API_Call<AgentMethod>(
        API_ENDPOINTS.AGENTS.METHODS,
        'GET',
        undefined,
        { params: { Agent_ID } }
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to load agent methods');
      }

      if (response.data?.Records) {
        setAgentMethods(response.data.Records);
      }
    } catch (err) {
      console.error('Error loading agent methods:', err);
      setError(err instanceof Error ? err.message : 'Failed to load agent methods');
      setAgentMethods([]); // Empty state - fail loud, don't hide failure
    } finally {
      setLoadingMethods(false);
    }
  };

  // Handle agent selection
  const handleAgentClick = (agentId: string) => {
    setSelectedAgent(agentId);
    loadAgentMethods(agentId);
  };

  // Handle capability pack launch
  const handlePackLaunch = (pack: CapabilityPack) => {
    console.log('Launching capability pack:', pack.slashCommand);

    /**
     * IMPLEMENTATION PLAN: Workflow Execution Integration
     *
     * This feature requires integrating with the multi-agent orchestration system.
     *
     * Required steps:
     * 1. Import workflow execution API from '../services/api/workflows.ts'
     * 2. Create endpoint: POST /api/workflows/execute
     *    - Request: { workflowId: string, slashCommand: string, parameters?: Record<string, any> }
     *    - Response: { executionId: string, status: string, agents: string[] }
     * 3. Show loading modal during execution
     * 4. Poll execution status or use WebSocket for real-time updates
     * 5. Navigate to execution details page: /workflows/executions/{executionId}
     *
     * Reference: .equilateral-standards/multi-agent-orchestration/workflow_execution_standards.md
     *
     * For now, showing alert to indicate the workflow that would be launched.
     */
    alert(`Launching workflow: ${pack.slashCommand}\n\nThis will execute the ${pack.name} capability pack.`);
  };

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'configs') {
      loadAgents();
    } else if (activeTab === 'packs') {
      loadCapabilityPacks();
    } else if (activeTab === 'results') {
      loadABResults();
    } else if (activeTab === 'analysis') {
      loadCostAccuracy();
    } else if (activeTab === 'topology') {
      loadTopology();
    }
  }, [activeTab]);

  // Get unique categories for filtering
  const categories = Array.from(
    new Set(agents.map((agent) => agent.model_configuration?.default_model?.provider || 'unknown'))
  );

  // Filter agents
  const filteredAgents = agents.filter((agent) => {
    const matchesCategory =
      filterCategory === 'all' ||
      agent.model_configuration?.default_model?.provider === filterCategory;
    const matchesSearch =
      searchQuery === '' ||
      agent.agent_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.agent_id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <BeakerIcon className="h-8 w-8 text-indigo-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Model Configuration A/B/C Testing
            </h1>
            <p className="text-gray-600">
              Manage agent model configurations and analyze A/B test performance
            </p>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">
                <span className="font-medium">Error:</span> {error}
              </p>
            </div>
            <div className="ml-auto pl-3">
              <button
                type="button"
                className="inline-flex text-red-800 hover:text-red-600"
                onClick={() => setError(null)}
              >
                <span className="sr-only">Dismiss</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('configs')}
              className={`${
                activeTab === 'configs'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <CircleStackIcon className="h-5 w-5 mr-2" />
              Agent Configurations
            </button>
            <button
              onClick={() => setActiveTab('packs')}
              className={`${
                activeTab === 'packs'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <SparklesIcon className="h-5 w-5 mr-2" />
              Capability Packs
            </button>
            <button
              onClick={() => setActiveTab('results')}
              className={`${
                activeTab === 'results'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <ChartBarIcon className="h-5 w-5 mr-2" />
              A/B Test Results
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              className={`${
                activeTab === 'analysis'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <CurrencyDollarIcon className="h-5 w-5 mr-2" />
              Cost/Accuracy Analysis
            </button>
            <button
              onClick={() => setActiveTab('topology')}
              className={`${
                activeTab === 'topology'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <svg
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Topology Graph
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Tab 1: Agent Configurations */}
          {activeTab === 'configs' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Agent Model Configurations</h2>

                {/* Filter and Search Controls */}
                <div className="flex gap-3">
                  <div className="flex items-center gap-2">
                    <FunnelIcon className="h-5 w-5 text-gray-500" />
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="all">All Categories</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    type="text"
                    placeholder="Search agents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {loadingAgents ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : filteredAgents.length === 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    {searchQuery || filterCategory !== 'all'
                      ? 'No agents match your filters'
                      : 'No agent configurations found'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredAgents.map((agent) => {
                    const config = agent.model_configuration;
                    const defaultModel = config?.default_model?.model || 'N/A';
                    const abEnabled = config?.ab_testing?.enabled || false;
                    const circuitBreaker = agent.circuit_breaker_status || 'closed';

                    return (
                      <div
                        key={agent.agent_id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-lg font-semibold">{agent.agent_name}</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              <strong>Default Model:</strong> {defaultModel}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                abEnabled
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {abEnabled ? 'A/B Testing Enabled' : 'A/B Testing Disabled'}
                            </span>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                circuitBreaker === 'closed'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              Circuit Breaker: {circuitBreaker}
                            </span>
                          </div>
                        </div>

                        {/* Show variants if A/B testing is enabled */}
                        {abEnabled && config.ab_testing?.variants && (
                          <div className="mt-4 p-3 bg-gray-50 rounded">
                            <div className="grid grid-cols-3 gap-4">
                              {config.ab_testing.variants.A && (
                                <div>
                                  <strong className="text-sm">Variant A:</strong>
                                  <p className="text-sm">{config.ab_testing.variants.A.model}</p>
                                  <p className="text-xs text-gray-500">
                                    {config.ab_testing.variants.A.traffic_percent}% traffic
                                  </p>
                                </div>
                              )}
                              {config.ab_testing.variants.B && (
                                <div>
                                  <strong className="text-sm">Variant B:</strong>
                                  <p className="text-sm">{config.ab_testing.variants.B.model}</p>
                                  <p className="text-xs text-gray-500">
                                    {config.ab_testing.variants.B.traffic_percent}% traffic
                                  </p>
                                </div>
                              )}
                              {config.ab_testing.variants.C && (
                                <div>
                                  <strong className="text-sm">Variant C:</strong>
                                  <p className="text-sm">{config.ab_testing.variants.C.model}</p>
                                  <p className="text-xs text-gray-500">
                                    {config.ab_testing.variants.C.traffic_percent}% traffic
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* View Methods Button */}
                        <div className="mt-4 border-t border-gray-200 pt-4">
                          <button
                            onClick={() => handleAgentClick(agent.agent_id)}
                            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            View Agent Methods
                          </button>
                        </div>

                        {/* Expanded Agent Methods */}
                        {selectedAgent === agent.agent_id && (
                          <div className="mt-4 border-t border-gray-200 pt-4">
                            <AgentMethodsDisplay
                              agentId={agent.agent_id}
                              agentName={agent.agent_name}
                              methods={agentMethods}
                              isLoading={loadingMethods}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Capability Packs */}
          {activeTab === 'packs' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Capability Packs</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Pre-configured workflows with specialized agents for common use cases
                  </p>
                </div>
              </div>

              {loadingPacks ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : capabilityPacks.length === 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">No capability packs available</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {capabilityPacks.map((pack) => (
                    <CapabilityPackCard
                      key={pack.id}
                      pack={pack}
                      onLaunch={handlePackLaunch}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab 3: A/B Test Results */}
          {activeTab === 'results' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">A/B Test Performance (Last 30 Days)</h2>

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm text-gray-600">Total Executions</h3>
                  <p className="text-2xl font-bold">{totalExecutions.toLocaleString()}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm text-gray-600">Average Success Rate</h3>
                  <p className="text-2xl font-bold">{(avgSuccessRate * 100).toFixed(1)}%</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm text-gray-600">Total Cost (USD)</h3>
                  <p className="text-2xl font-bold">${totalCost.toFixed(2)}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm text-gray-600">Average Cost per Call</h3>
                  <p className="text-2xl font-bold">${avgCost.toFixed(4)}</p>
                </div>
              </div>

              {/* Results Table */}
              {loadingAbResults ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : abResults.length === 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    No A/B test results found for the last 30 days
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Agent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Method
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Model
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Executions
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Success Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Avg Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Cost
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {abResults.map((result, idx) => (
                        <tr key={idx}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {result.agent_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {result.method_name || 'All'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {result.variant_name}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {result.model_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {parseInt(result.execution_count.toString()).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                                <div
                                  className="bg-blue-600 h-2.5 rounded-full"
                                  style={{ width: `${result.success_rate * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-sm">{(result.success_rate * 100).toFixed(1)}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ${parseFloat(result.avg_cost_usd.toString()).toFixed(4)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ${parseFloat(result.total_cost_usd.toString()).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Cost/Accuracy Analysis */}
          {activeTab === 'analysis' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Cost vs Accuracy Optimization (Last 7 Days)</h2>
              <p className="text-gray-600">
                Higher cost/accuracy ratio = better performance per dollar spent
              </p>

              {loadingCostAccuracy ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : costAccuracyResults.length === 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    No cost/accuracy data available for the last 7 days
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Agent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Method
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Avg Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Accuracy
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cost/Accuracy Ratio
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sample Size
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {costAccuracyResults.map((result, idx) => (
                        <tr key={idx}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {result.agent_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {result.method_name || 'All'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {result.variant_name}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ${parseFloat(result.avg_cost.toString()).toFixed(4)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(parseFloat(result.accuracy.toString()) * 100).toFixed(1)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {parseFloat(result.cost_accuracy_ratio.toString()).toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {parseInt(result.sample_size.toString()).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Tab 5: Topology Graph */}
          {activeTab === 'topology' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Agent Topology Graph</h2>
              <p className="text-gray-600">
                Interactive visualization of agent relationships and dependencies
              </p>

              {loadingTopology ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 min-h-[600px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    <p className="mt-4 text-sm text-gray-500">Loading topology...</p>
                  </div>
                </div>
              ) : (
                <TopologyGraph topology={topologyData} width={1200} height={600} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModelConfigPage;
